import { z } from 'zod';
import { uuidSchema, timestampSchema } from './common';
import { billingPlanTypeSchema, planKeySchema, type BillingPlanType, type PlanKey } from './billing';
import { isIsoTimestamp } from '../time';
import { MAX_EVAL_RETENTION_DAYS } from './setup/hubs';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// --- Param schemas ---

export const adminOrgIdParam = z.object({
  orgId: uuidSchema,
});

/**
 * `:orgId` of the voice-calls allowlist routes, lowercased at the ingress. Org ids are
 * minted lowercase and the gate matches exactly, so a mis-cased id stored as typed would
 * be reported allowed while its org stayed dark.
 */
export const adminVoiceCallsOrgIdParam = z.object({
  orgId: uuidSchema.transform((id) => id.toLowerCase()),
});

export const adminOrgAdminIdParam = z.object({
  orgId: uuidSchema,
  // `adminId` is a `member_grant.user_id`, which for a logged-in user is the
  // WorkOS `sub` (`user_01…`), not a UUID. Matches `orgAdminIdParam` on the
  // org-facing route.
  adminId: z.string().min(1),
});

export const adminUserIdParam = z.object({
  userId: uuidSchema,
});

/**
 * A seeded `pricing_plan_id` — `pp-` plus a lowercase slug (`pp-free`, `pp-solo`,
 * `pp-team`, `pp-growth-pack`). That is the id format every live row carries; see
 * `apps/backend/src/durable-objects/platform-do/seed.ts`.
 */
const PRICING_PLAN_ID_RE = /^pp-[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * A UUID is still accepted beside the slug: rows minted before the `pp-` naming (the
 * seed deactivates them but keeps them for audit) and any row `upsertPricingPlan`
 * created through its `crypto.randomUUID()` fallback are addressed by UUID. Deliberately
 * NOT a bare string — the id selects the row this route writes, so an unconstrained
 * param is how a typo reaches storage.
 */
export const adminPlanIdParam = z.object({
  planId: z.string().refine(
    (value) => PRICING_PLAN_ID_RE.test(value) || uuidSchema.safeParse(value).success,
    { message: 'planId must be a pricing plan id such as "pp-free", or a UUID' },
  ),
});

// --- Query schemas ---

export const adminOrganizationsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional().default(''),
});

export const adminUserSearchQuery = z.object({
  email: z.string().min(3, 'email query parameter must be at least 3 characters'),
});

// One-time kanban slug migration query. Default is dry-run for safety.
export const kanbanSlugMigrationQuery = z.object({
  dry_run: z.enum(['true', 'false']).optional(),
});

// --- Body schemas ---

export const adjustQuotaBody = z.object({
  metric_type: z.string().min(1, 'metric_type is required'),
  action: z.enum(['add', 'set']),
  value: z.number().int().min(0, 'value must be a non-negative number'),
});

/**
 * PATCH /api/admin/organizations/:orgId/plan — both fields optional, at least one
 * required. `plan_type` stayed mandatory only because it was the sole field; a
 * window-only edit must not be forced to restate a plan it is not changing, and the
 * route relies on absence (never `undefined`) to leave `plan_type` out of the OrgDO
 * write entirely.
 */
export const updatePlanBody = z.object({
  /**
   * `comped` is the operator's lever, not `paid`. `paid` means "Stripe is billing this
   * org", and the route refuses it on a row with no billing period for that reason — the
   * shape it used to accept is the one that left an org unprojectable to Rekor and 503'd
   * every Data/Bases provisioning operation. It stays in the enum only to repair a row
   * whose Stripe period exists but whose `plan_type` drifted.
   */
  plan_type: billingPlanTypeSchema.optional(),
  /**
   * What the org is entitled TO, once `plan_type` says how it is paid for. Set it beside
   * `plan_type: 'comped'` to grant a paid plan's allowances without a subscription; the
   * Rekor projection reads the pricing row for this key. Stripe owns this column on a
   * `paid` row, so the route refuses to move it there.
   */
  plan_key: planKeySchema.optional(),
  /**
   * Move the start of the org's rolling window. The operator lever for a window that
   * must be backdated — `free_plan_started_at` is otherwise only ever stamped to *now*
   * (org creation, `cancelBilling`, entering `comped`, the lazy rollover), so an elapsed
   * window is unreachable without it.
   *
   * Canonical form only (AGENTS.md › Timestamps): `timestampSchema` already refuses a
   * non-UTC offset and `isIsoTimestamp` pins the millisecond precision, because this
   * value is stored as-is and read back by `rollingWindowStart()` / `rollingWindowEnd()`
   * and by the Rekor projection's `window_start`.
   *
   * Past-or-now only. A future start freezes the org's window permanently:
   * `maybeRolloverBillingWindow` derives a NEGATIVE elapsed and so never archives the
   * metrics nor restamps the column, while the rollover sweep selects only windows
   * that have already passed — so nothing rolls the org over, its consumed operations
   * never reset, and past quota every AI turn is refused until a human re-patches.
   */
  free_plan_started_at: timestampSchema
    .refine(isIsoTimestamp, {
      message: 'free_plan_started_at must be ISO-8601 UTC with millisecond precision (e.g. 2026-07-01T00:00:00.000Z)',
    })
    .refine((value) => Date.parse(value) <= Date.now(), {
      message: 'free_plan_started_at must not be in the future',
    })
    .optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

export const addAdminUserBody = z.object({
  user_email: z.string().email('user_email must be a valid email address'),
});

export const updatePlatformConfigBody = z.object({
  free_plan_max_free_orgs_per_user: z.number().int().min(0).optional(),
  /** Kill switch for admin debug-read endpoints. 0 = off, 1 = on. */
  debug_read_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * The harness rollout gate + kill switch — the ONLY harness gate. 0 = off
   * (default-deny: agent create/update is refused AND the per-turn kill switch
   * aborts every harness turn on its next invocation), 1 = on. `wayai admin harness
   * disable|enable` flips this. The DO's `updatePlatformConfig` allowlist already
   * accepts it; this exposes it on the route.
   */
  harness_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * Default eval-session retention window in days, used by every hub that sets no
   * `eval_retention_days` override. `0` disables eval retention platform-wide.
   * NOT NULL in the DDL, so the field is non-nullable here — clearing is not a
   * state; setting 0 is.
   */
  default_eval_retention_days: z.number().int().min(0).max(MAX_EVAL_RETENTION_DAYS).optional(),
  /**
   * The voice-calls kill switch, the platform half of the voice-calls rollout gate.
   * 0 = off (the default: every voice-call surface stays dark for every org), 1 = on.
   * On its own it opens nothing: an org must also be on `voice_calls_org_allowlist`,
   * which this body cannot write (`PUT`/`DELETE /admin/voice-calls/allowlist/:orgId`
   * change one org at a time). `wayai admin voice-calls enable|disable` flips this.
   */
  voice_calls_enabled: z.number().int().min(0).max(1).optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

/**
 * The most orgs the voice-calls allowlist holds. The list rides the KV-cached
 * `platform_config` row, which every platform-config reader parses, so it is bounded:
 * at the cap it adds about 8 KB to that row.
 */
export const MAX_VOICE_CALLS_ALLOWLIST_ORGS = 200;

/** `platform_config.voice_calls_org_allowlist` as stored: a JSON array of org UUIDs. */
export const voiceCallsOrgAllowlistSchema = z.array(uuidSchema).max(MAX_VOICE_CALLS_ALLOWLIST_ORGS);

/**
 * Read the stored voice-calls allowlist. Fail-closed: a value that is not a well-formed
 * list (absent, not JSON, not an array of UUIDs, over the cap) reads as the empty list,
 * which admits no org.
 */
export function parseVoiceCallsOrgAllowlist(raw: unknown): string[] {
  if (typeof raw !== 'string') return [];
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  const parsed = voiceCallsOrgAllowlistSchema.safeParse(value);
  return parsed.success ? parsed.data : [];
}

export const updateFreeOrgLimitBody = z.object({
  max_free_orgs_override: z.number().int().min(0).nullable(),
});

/**
 * The scope/id shape shared by the platform-admin fan-out endpoints
 * (`POST /admin/harness/mass-destroy`, `POST /admin/hubs/repair-team-orphans`).
 *
 * Exactly ONE id must match `scope` (enforced by the superRefine): `org` needs
 * `org_id` (and no `hub_id`); `hub` needs `hub_id` (and no `org_id`); `all` takes
 * neither. `dry_run` reports the blast radius without acting — run it first.
 * `.strict()` rejects unknown keys so a typo'd scope-key can't widen the target.
 */
export const adminScopedTargetBody = z
  .object({
    scope: z.enum(['all', 'org', 'hub']),
    org_id: z.string().uuid().optional(),
    hub_id: z.string().optional(),
    dry_run: z.boolean().optional().default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.scope === 'org') {
      if (!data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org requires org_id', path: ['org_id'] });
      }
      if (data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org must not include hub_id', path: ['hub_id'] });
      }
    } else if (data.scope === 'hub') {
      if (!data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub requires hub_id', path: ['hub_id'] });
      }
      if (data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub must not include org_id', path: ['org_id'] });
      }
    } else {
      // scope === 'all' — platform-wide, so neither id is allowed.
      if (data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include org_id', path: ['org_id'] });
      }
      if (data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include hub_id', path: ['hub_id'] });
      }
    }
  });

/**
 * POST /api/admin/harness/mass-destroy — the operator incident-response reap
 * (harness-agents PR 2.9b). Enumerates live harness sandboxes/slots in `scope`
 * and reaps each conversation's sandbox + MCP token + concurrency slot on demand.
 * Complements the automated per-turn kill switch (PR 2.9a), which only fires when
 * a conversation next turns; this reclaims what is live RIGHT NOW.
 */
export const adminHarnessMassDestroyBody = adminScopedTargetBody;

/**
 * POST /api/admin/hubs/repair-team-orphans — purge `hub_team_id` references left
 * dangling by a team deletion that predates the `deleteHubTeam` cascade, and clear
 * the removed memberships' UserDO mirror. Idempotent; `dry_run` first.
 */
export const adminRepairTeamOrphansBody = adminScopedTargetBody;

/**
 * POST /api/admin/organizations/repair-legacy-grants — enumerate and repair the
 * `member_grant` rows written by the platform-admin org-admin add before #3922
 * (the email landed in the `user_id` column and `user_email` stayed NULL).
 *
 * Org-level only, so this does NOT reuse `adminScopedTargetBody`: there is no
 * `hub` scope to give it, and accepting one would imply a hub roster that does
 * not exist. Idempotent; `dry_run` first.
 */
export const adminRepairLegacyOrgGrantsBody = z
  .object({
    scope: z.enum(['all', 'org']),
    org_id: z.string().uuid().optional(),
    dry_run: z.boolean().optional().default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.scope === 'org' && !data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org requires org_id', path: ['org_id'] });
    }
    if (data.scope === 'all' && data.org_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include org_id', path: ['org_id'] });
    }
  });

/**
 * POST /api/admin/organizations/repair-comped-plans — the heal owed beside the plan
 * PATCH's refusal. Dry-run by default, and `org_id` narrows the walk to
 * one organization so a repair can be rehearsed on a single row before the fleet.
 */
export const adminRepairCompedPlansBody = z
  .object({
    org_id: z.string().uuid().optional(),
    dry_run: z.boolean().optional().default(true),
  })
  .strict();

export const updatePricingPlanBody = z.object({
  display_name: z.string().min(1).optional(),
  stripe_product_id: z.string().optional(),
  lookup_key_prefix: z.string().optional(),
  messages_per_unit: z.number().int().min(1, 'messages_per_unit must be a positive integer').optional(),
  seats_per_unit: z.number().int().min(1, 'seats_per_unit must be a positive integer').optional(),
  seats_scaling: z.enum(['fixed', 'per_quantity']).optional(),
  is_active: z.boolean().optional(),
  // Rekor entitlement allowances. Editable here for the
  // same reason `messages_per_unit` is — and this route is the only ROUTINE way to change an
  // allowance on a live plan: the seed establishes a plan row and never rewrites one, so a
  // seed-version bump no longer moves these values (see `seed.ts`).
  //
  // It is not the only writer, and an operator should not read it as authoritative against
  // one. A dated owner pricing decision may also land as a bounded `PLATFORM_MIGRATIONS`
  // step naming its columns and rows, and such a step CAN supersede an edit made here —
  // deliberately, since an owner decision outranks an earlier operator edit of the same
  // field. `seed.ts`'s header calls this shape 2 and states its replay residual; v28 is the
  // first instance (`rekor_production_bases` on `free`, 2026-08-19).
  rekor_ops_tenths_included: z.number().int().min(0).optional(),
  rekor_max_records: z.number().int().min(0).optional(),
  rekor_import_rows_included: z.number().int().min(0).optional(),
  rekor_production_bases: z.number().int().min(0).max(1).optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type AdjustQuotaInput = z.infer<typeof adjustQuotaBody>;
export type UpdatePlanInput = z.infer<typeof updatePlanBody>;
export type AddAdminUserInput = z.infer<typeof addAdminUserBody>;
export type UpdatePlatformConfigInput = z.infer<typeof updatePlatformConfigBody>;
export type UpdateFreeOrgLimitInput = z.infer<typeof updateFreeOrgLimitBody>;
export type UpdatePricingPlanInput = z.infer<typeof updatePricingPlanBody>;
export type AdminHarnessMassDestroyInput = z.infer<typeof adminHarnessMassDestroyBody>;
export type AdminRepairTeamOrphansInput = z.infer<typeof adminRepairTeamOrphansBody>;
export type AdminRepairLegacyOrgGrantsInput = z.infer<typeof adminRepairLegacyOrgGrantsBody>;
export type AdminRepairCompedPlansInput = z.infer<typeof adminRepairCompedPlansBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** GET /admin/check */
export interface AdminCheckResponse {
  is_platform_admin: true;
}

/** Billing metric row returned inside organization list/detail */
export interface AdminBillingMetric {
  organization_id: string;
  metric_type: string;
  monthly_included: number;
  consumed_subscription: number;
}

/** Organization item in the paginated list */
export interface AdminOrganizationItem {
  organization_id: string;
  organization_name: string;
  created_at: string;
  plan_type: string;
  billing_status: string;
  has_subscription: boolean;
  period_end: string | null;
  owner_email: string | null;
  admin_users: Array<{
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  }>;
  metrics: AdminBillingMetric[];
}

/** Pagination metadata */
export interface AdminPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** GET /admin/organizations */
export interface AdminOrganizationsListResponse {
  data: AdminOrganizationItem[];
  pagination: AdminPaginationMeta;
}

/** POST /admin/organizations/:orgId/resync-registry */
export interface AdminResyncOrgRegistryResponse {
  ok: true;
  org_id: string;
  patched_fields: string[];
}

/** POST /admin/dev/backfill-org-registry */
export interface AdminBackfillOrgRegistryResponse {
  processed: number;
  succeeded: number;
  failed: number;
  failures: Array<{ org_id: string; error: string }>;
}

/** GET /admin/organizations/:orgId */
export interface AdminOrganizationDetailResponse {
  data: {
    organization_id: string;
    billing: Record<string, unknown> | null;
    metrics: AdminBillingMetric[];
    admin_users: Array<{
      organization_admin_user_id: string;
      user_email: string;
      is_owner: boolean;
    }>;
  };
}

/** POST /admin/organizations/:orgId/admins */
export interface AdminAddAdminUserResponse {
  data: {
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  };
}

/** GET /admin/config, PATCH /admin/config */
export interface AdminConfigResponse {
  data: Record<string, unknown>;
}

/** PUT /admin/voice-calls/allowlist/:orgId, DELETE /admin/voice-calls/allowlist/:orgId */
export interface AdminVoiceCallsAllowlistResponse {
  data: {
    org_id: string;
    /** Whether the org is on the allowlist after the call. */
    allowed: boolean;
    /** False when the org was already in that state, so nothing was written. */
    changed: boolean;
    /** The whole allowlist after the call. */
    org_ids: string[];
  };
}

/** User search result item */
export interface AdminUserSearchItem {
  user_id: string;
  user_email: string;
  profile_name: string | null;
  max_free_orgs_override: number | null;
}

/** GET /admin/users/search */
export interface AdminUserSearchResponse {
  data: AdminUserSearchItem[];
}

/** PATCH /admin/users/:userId/free-org-limit */
export interface AdminUpdateFreeOrgLimitResponse {
  data: AdminUserSearchItem;
}

/** Per-conversation reap outcome inside the mass-destroy response. NEVER carries tokens/secrets. */
export interface AdminHarnessMassDestroyDetail {
  hub_id: string;
  conversation_id: string;
  /** True when the slot/session had a live sandbox_id (a sandbox was in flight). */
  had_sandbox: boolean;
  /** True when the sandbox destroy succeeded (false ⇒ best-effort reap failed; cron backstop reclaims). */
  sandbox_destroyed: boolean;
  /** True when the harness namespace was present and reaped (false on a dry_run or an idempotent no-op). */
  reaped: boolean;
}

/** One hub's dangling-team-reference repair (POST /admin/hubs/repair-team-orphans). */
export interface AdminRepairTeamOrphansDetail {
  hub_id: string;
  /** Team ids still referenced by a row after their `hub_team` was deleted. */
  dangling_team_ids: string[];
  /** Memberships purged (or, on a dry_run, that would be) — HubDO row + UserDO mirror. */
  team_users_removed: number;
}

/** POST /admin/hubs/repair-team-orphans — aggregate repair report. */
export interface AdminRepairTeamOrphansResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    hubs_scanned: number;
    /** Hubs whose repair RPC threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. Same signal, one level up. */
    orgs_errored: number;
    /** Hubs found carrying at least one dangling team reference. */
    hubs_repaired: number;
    /** Memberships purged across all hubs (on a dry_run, the count that WOULD be purged). */
    team_users_removed: number;
    /** Per-hub breakdown; only hubs with something to repair are listed. */
    details: AdminRepairTeamOrphansDetail[];
  };
}

/** One legacy roster row's disposition (POST /admin/organizations/repair-legacy-grants). */
export interface AdminRepairLegacyOrgGrantsDetail {
  org_id: string;
  /**
   * The email the row is keyed by — it sits in `member_grant.user_id`, not
   * `user_email`. Reported verbatim, NOT normalized: the operator matches this
   * against the roster, where a mixed-case key is stored as it was written.
   */
  user_email: string;
  /**
   * On a `dry_run` this is what WOULD happen; nothing is written.
   *   `repaired` — the email resolved to a real user: both halves of access written
   *                (OrgDO roster + UserDO membership) and the legacy row collapsed.
   *   `dropped`  — no user account for that email. The row conferred no access and
   *                only cost a seat, so it is deleted.
   *   `skipped`  — the row carries an owner grant, which this pass will not rewrite
   *                or delete (see the route's owner guard). Left for manual review.
   *   `errored`  — the repair threw; the row is unchanged. Re-run to retry it.
   */
  action: 'repaired' | 'dropped' | 'skipped' | 'errored';
  /** The resolved UserDO id on `repaired`; null for every other action. */
  user_id: string | null;
  /** Why, for `skipped` and `errored`. */
  reason?: string;
}

/** POST /admin/organizations/repair-legacy-grants — aggregate repair report. */
export interface AdminRepairLegacyOrgGrantsResponse {
  data: {
    scope: 'all' | 'org';
    dry_run: boolean;
    orgs_scanned: number;
    /** Orgs whose roster read threw and were skipped (partial-failure signal — re-run to cover them). */
    orgs_errored: number;
    /** Legacy rows found in scope; equals repaired + dropped + skipped + errored. */
    rows_found: number;
    rows_repaired: number;
    rows_dropped: number;
    rows_skipped: number;
    rows_errored: number;
    /** Per-row breakdown; only legacy rows are listed, so a healthy scope returns []. */
    details: AdminRepairLegacyOrgGrantsDetail[];
  };
}

/** One organization the comped-plan repair examined and changed. */
export interface AdminRepairCompedPlansDetail {
  org_id: string;
  /**
   * What happened to this row. `candidate` is every row under `dry_run` — matched, nothing
   * written. Explicit rather than inferred from the presence of `reason`, so the counts
   * below and the report agree without the reader reconstructing the rule.
   */
  outcome: 'candidate' | 'repaired' | 'errored';
  /**
   * Which unprojectable shape was found: no Stripe period at all, or one that has already
   * ended (a cancelled subscription's date, which `cancelBilling` leaves in place).
   */
  found: 'paid_without_period' | 'paid_with_ended_period';
  /** What the repair wrote (or would write, under `dry_run`). */
  plan_type: BillingPlanType;
  plan_key: PlanKey;
  /**
   * What the entitlement push driven by this repair's billing write did. Absent when
   * nothing was written (a `dry_run` candidate, or an errored row).
   *
   * - `not_owed` — the org is not Rekor-bearing, so no projection is owed yet. The expected
   *   result for the incident's own class: an org that never adopted a base, because every
   *   provisioning call was refused. The shape is fully repaired; nothing to chase.
   * - `pushed` — a fresh projection was recorded.
   * - `not_pushed` — a projection was owed and none landed. The sweep converges it, and it
   *   is the signal to look at the push logs.
   */
  push?: 'not_owed' | 'pushed' | 'not_pushed';
  /** Why, for `errored`. */
  reason?: string;
}

/** POST /admin/organizations/repair-comped-plans — aggregate repair report. */
export interface AdminRepairCompedPlansResponse {
  data: {
    dry_run: boolean;
    /** Candidate orgs examined — the registry-narrowed set, not the whole registry. */
    orgs_scanned: number;
    /** Orgs whose billing read threw and were skipped (re-run to cover them). */
    orgs_errored: number;
    /** Unprojectable rows confirmed in scope; equals `details.length`. */
    rows_found: number;
    /** Rows written. Always 0 under `dry_run`, where every row is a `candidate`. */
    rows_repaired: number;
    rows_errored: number;
    /** Per-org breakdown; only matched rows are listed, so a healthy fleet returns []. */
    details: AdminRepairCompedPlansDetail[];
  };
}

/** POST /admin/harness/mass-destroy — aggregate reap report. */
export interface AdminHarnessMassDestroyResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    /** Distinct hubs scanned for live harness slots. */
    hubs_scanned: number;
    /** Hubs whose slot enumeration threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Orgs whose hub listing threw, so their hubs never entered the scan. Same signal, one level up. */
    orgs_errored: number;
    /** Live harness sessions discovered across all successfully-scanned hubs. */
    sessions_found: number;
    /** Sessions actually reaped (0 on a dry_run). */
    sessions_reaped: number;
    /** Sessions whose reap RPC threw and were skipped (0 on a dry_run; re-run to cover them). */
    sessions_errored: number;
    /** Sandboxes successfully destroyed (0 on a dry_run). */
    sandboxes_destroyed: number;
    details: AdminHarnessMassDestroyDetail[];
  };
}

/** GET /admin/pricing-plans */
export interface AdminPricingPlansListResponse {
  data: Array<Record<string, unknown>>;
}

/** PATCH /admin/pricing-plans/:planId */
export interface AdminUpdatePricingPlanResponse {
  data: Record<string, unknown>;
}

// One-time kanban slug migration result.
export interface KanbanSlugMigrationResult {
  dry_run: boolean;
  total_hubs: number;
  migrated: number;
  already_migrated: number;
  skipped: Array<{ hub_id: string; reason: string }>;
}

/** POST /admin/migrate/kanban-slugs */
export interface KanbanSlugMigrationResponse {
  data: KanbanSlugMigrationResult;
}

// =============================================================================
// DATA EXPLORER — Request Schemas
// =============================================================================

export const dataExplorerSearchQuery = z.object({
  q: z.string().min(1, 'search query is required'),
  type: z.enum(['org', 'hub', 'user']).optional(),
});

export const dataExplorerOrgIdParam = z.object({ orgId: uuidSchema });
export const dataExplorerHubIdParam = z.object({ hubId: uuidSchema });
export const dataExplorerConvIdParam = z.object({ convId: uuidSchema });

export const dataExplorerDoInstancesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  orphans_only: z.coerce.boolean().optional().default(false),
  /** Direct lookup by entity UUID (hub_id/org_id/user_id/conversation_id). When set, bypasses pagination and returns a synthesized entry via idFromName. */
  search: z.string().optional(),
});

export const dataExplorerNsIdParam = z.object({
  nsId: z.string().min(1, 'namespace ID is required'),
});

export const dataExplorerKvKeysBody = z.object({
  prefix: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const dataExplorerKvKeyBody = z.object({
  key: z.string().min(1, 'key is required'),
});

/** Accepts UUID or Cloudflare DO hex ID (for orphan deletion) */
const uuidOrHexId = z.string().min(1);
export const dataExplorerDeleteOrgParam = z.object({ orgId: uuidOrHexId });
export const dataExplorerDeleteHubParam = z.object({ hubId: uuidOrHexId });
export const dataExplorerDeleteConvParam = z.object({ convId: uuidOrHexId });
export const dataExplorerDeleteUserParam = z.object({ userId: uuidOrHexId });

export const dataExplorerDeleteQuery = z.object({
  confirmation: z.string().min(1, 'confirmation is required'),
  cascade: z.coerce.boolean().optional().default(false),
});

export const dataExplorerKvDeleteBody = z.object({
  key: z.string().min(1, 'key is required'),
  confirmation: z.string().min(1, 'confirmation is required'),
});

// =============================================================================
// DATA EXPLORER — Inferred Types
// =============================================================================

export type DataExplorerSearchInput = z.infer<typeof dataExplorerSearchQuery>;
export type DataExplorerDeleteInput = z.infer<typeof dataExplorerDeleteQuery>;
export type DataExplorerKvKeysInput = z.infer<typeof dataExplorerKvKeysBody>;
export type DataExplorerKvKeyInput = z.infer<typeof dataExplorerKvKeyBody>;
export type DataExplorerKvDeleteInput = z.infer<typeof dataExplorerKvDeleteBody>;

// =============================================================================
// DATA EXPLORER — Response Types
// =============================================================================

/** Hub entry in org entity tree */
export interface DataExplorerHubEntry {
  hub_id: string;
  hub_name: string;
  hub_type: string;
  hub_environment: string;
  production_hub_id: string | null;
  dashboard_url: string | null;
}

/** Search result item */
export interface DataExplorerSearchResultItem {
  type: 'org' | 'hub' | 'user';
  id: string;
  name: string;
  /** Extra context (e.g., org name for hub results, email for user results) */
  context?: string;
}

/** GET /data-explorer/search */
export interface DataExplorerSearchResponse {
  data: DataExplorerSearchResultItem[];
}

/** GET /data-explorer/entity/org/:orgId */
export interface DataExplorerOrgEntityResponse {
  data: {
    organization_id: string;
    organization_name: string;
    dashboard_url: string | null;
    hubs: DataExplorerHubEntry[];
    /** Redacted via `SENSITIVE_COLUMNS.org.billing` before leaving the Worker. */
    billing: Record<string, unknown> | null;
    /** Column names masked in `billing`, so the UI can show the same footer the debug reads do. */
    redacted?: string[];
  };
}

/** GET /data-explorer/entity/hub/:hubId */
export interface DataExplorerHubEntityResponse {
  data: {
    hub_id: string;
    hub_name: string;
    hub_type: string;
    dashboard_url: string | null;
    conversation_count: number;
  };
}

/** GET /data-explorer/entity/conversation/:convId */
export interface DataExplorerConvEntityResponse {
  data: {
    conversation_id: string;
    dashboard_url: string | null;
    /** Redacted via `SENSITIVE_COLUMNS.conversation.conversation` before leaving the Worker. */
    conversation: Record<string, unknown> | null;
    /** Column names masked in `conversation`, so the UI can show the same footer the debug reads do. */
    redacted?: string[];
  };
}

/** DO namespace item */
export interface DataExplorerDoNamespace {
  id: string;
  name: string;
  class: string;
}

/** GET /data-explorer/do/namespaces */
export interface DataExplorerDoNamespacesResponse {
  data: DataExplorerDoNamespace[];
}

/** DO instance item */
export interface DataExplorerDoInstance {
  id: string;
  name: string | null;
  /** `null` means the backend did not probe CF for storage state (e.g., direct search by UUID) */
  hasStoredData: boolean | null;
  dashboard_url: string;
  /** The domain entity ID (e.g., org_id, hub_id) used in idFromName — null if unknown */
  entity_id?: string | null;
  /** Resolved domain name (e.g., org name) — null if not resolved */
  resolved_name?: string | null;
  /** Whether this instance is an orphan (not in any known hierarchy) */
  is_orphan?: boolean;
}

/** GET /data-explorer/do/namespaces/:nsId/instances */
export interface DataExplorerDoInstancesResponse {
  data: DataExplorerDoInstance[];
  cursor?: string;
}

/** KV namespace item */
export interface DataExplorerKvNamespace {
  name: string;
  id: string;
}

/** GET /data-explorer/kv/namespaces */
export interface DataExplorerKvNamespacesResponse {
  data: DataExplorerKvNamespace[];
}

/** KV key item */
export interface DataExplorerKvKeyItem {
  /** Display name. Masked when the key itself is credential material. */
  name: string;
  metadata: Record<string, unknown> | null;
  expiration?: number;
  /**
   * True when `name` was masked, so it is NOT a usable key identity. Clients must
   * not send it back as a view/delete target — the real key never leaves the
   * Worker for these entries.
   */
  masked?: boolean;
}

/** POST /data-explorer/kv/namespaces/:nsId/keys/list */
export interface DataExplorerKvKeysResponse {
  data: DataExplorerKvKeyItem[];
  cursor?: string;
}

/** POST /data-explorer/kv/namespaces/:nsId/keys/value */
export interface DataExplorerKvValueResponse {
  data: {
    key: string;
    value: string | null;
    masked: boolean;
  };
}

/** DELETE responses */
export interface DataExplorerDeleteResult {
  deleted: string[];
  failed: Array<{ id: string; error: string }>;
}

export interface DataExplorerDeleteResponse {
  data: DataExplorerDeleteResult;
}

// =============================================================================
// PITR (Point-in-Time Recovery) — Request Schemas
// =============================================================================

/** Valid DO namespace names for PITR operations */
export const pitrDoNamespace = z.enum(['HUB_DO', 'CONVERSATION_DO', 'ORG_DO', 'USER_DO', 'PLATFORM_DO']);

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export const pitrBookmarkParam = z.object({
  namespace: pitrDoNamespace,
  instanceId: z.string().min(1, 'instanceId is required'),
});

/** POST /admin/pitr/:namespace/:instanceId/restore — reuses bookmark param shape */
export const pitrRestoreParam = pitrBookmarkParam;

/** Body for restore request */
export const pitrRestoreBody = z.object({
  /** ISO 8601 timestamp to restore to (must be within last 30 days) */
  target_timestamp: z.string().datetime('target_timestamp must be a valid ISO 8601 datetime'),
  /** Must match the instanceId for confirmation */
  confirmation: z.string().min(1, 'confirmation is required'),
  /** If true, only fetch the bookmark without performing the restore (default: true) */
  dry_run: z.boolean().default(true),
});

// =============================================================================
// PITR — Inferred Types
// =============================================================================

export type PitrDoNamespace = z.infer<typeof pitrDoNamespace>;
export type PitrRestoreInput = z.infer<typeof pitrRestoreBody>;

// =============================================================================
// PITR — Response Types
// =============================================================================

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export interface PitrBookmarkResponse {
  data: {
    namespace: string;
    instance_id: string;
    current_bookmark: string;
  };
}

/** POST /admin/pitr/:namespace/:instanceId/restore */
export interface PitrRestoreResponse {
  data: {
    namespace: string;
    instance_id: string;
    target_timestamp: string;
    target_bookmark: string;
    current_bookmark: string;
    /** Bookmark that can be used to undo this restore */
    undo_bookmark: string | null;
    dry_run: boolean;
    restore_scheduled: boolean;
  };
}

// =============================================================================
// DO Health Validation — Response Types
// =============================================================================

export interface HealthValidationIssue {
  table: string;
  check: 'existence' | 'row_count' | 'json_validity';
  severity: 'error' | 'warning';
  message: string;
}

export interface HealthValidationResponse {
  data: {
    status: 'pass' | 'fail';
    tables_checked: number;
    tables_passed: number;
    issues: HealthValidationIssue[];
    checked_at: string;
  };
}

// =============================================================================
// Health Sampling — Request Schemas
// =============================================================================

/** POST /admin/health-sampling — trigger body */
export const healthSamplingTriggerBody = z.object({
  /** Number of instances to sample per DO type (default: 10) */
  sample_size: z.number().int().min(1).max(100).default(10),
  /** Filter to specific DO types (default: all) */
  do_types: z.array(pitrDoNamespace).optional(),
});

export type HealthSamplingTriggerInput = z.infer<typeof healthSamplingTriggerBody>;

// =============================================================================
// DATA EXPLORER DEBUG READS — Request Schemas
// =============================================================================
//
// Platform-admin-only read-only inspection of DO SQLite state. Gated by
// platformAdminMiddleware + strict rate limit + kill switch + column redaction.
// See workers/backend/src/durable-objects/debug-redaction.ts for the central
// allowlist and redaction rules.

/** Valid DO types for debug reads. Shared source of truth — re-exported as `DoType` in debug-redaction.ts. */
export const dataExplorerDebugDoType = z.enum(['hub', 'org', 'conversation', 'user', 'platform']);

/** Hard cap on `limit` query param — applied in both Zod and the DO RPC. */
export const DEBUG_READ_MAX_LIMIT = 100;
/** Default `limit` when the caller omits it. */
export const DEBUG_READ_DEFAULT_LIMIT = 20;

export const DEBUG_DO_HEX_PREFIX = 'hex:';

/**
 * `hex:<64-hex>` is resolved via `idFromString` (raw CF DO id — for inspecting
 * DOs discovered without a reverse path, e.g. orphan-audit). All other forms
 * go through `idFromName`.
 */
const debugDoEntityId = z
  .string()
  .min(1, 'entity id is required')
  .max(68)
  .refine(
    (v) => !v.startsWith(DEBUG_DO_HEX_PREFIX) || /^hex:[0-9a-f]{64}$/.test(v),
    { message: 'hex: prefix must be followed by exactly 64 lowercase hex chars' },
  );

/**
 * Table name param. Two-layer injection defense: regex here + assertTableAllowed()
 * against a frozen allowlist in the DO RPC. Table names are NEVER string-interpolated
 * into SQL — they're looked up in a constant map.
 */
const debugTableName = z.string().regex(
  /^[a-z][a-z0-9_]{0,63}$/,
  'table name must be lowercase letters, digits, underscores (<=64 chars)',
);

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export const dataExplorerDebugTablesParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
});

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export const dataExplorerDebugRowsParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
  name: debugTableName,
});

export const dataExplorerDebugRowsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
  /** Optional lookup by primary key value — typed as string since PKs are all TEXT UUIDs */
  id: z.string().min(1).max(256).optional(),
});

// =============================================================================
// DATA EXPLORER DEBUG READS — Inferred Types
// =============================================================================

export type DataExplorerDebugDoType = z.infer<typeof dataExplorerDebugDoType>;
export type DataExplorerDebugRowsQueryInput = z.infer<typeof dataExplorerDebugRowsQuery>;

// =============================================================================
// DATA EXPLORER DEBUG READS — Response Types
// =============================================================================

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export interface DataExplorerDebugTablesResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    /** Tables present in the DO that are also in the debug allowlist */
    tables: string[];
  };
}

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export interface DataExplorerDebugRowsResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    table: string;
    /** Rows with sensitive columns replaced by the string "[REDACTED]" */
    rows: Record<string, unknown>[];
    /** Column names that were redacted in this response (for operator awareness) */
    redacted_columns: string[];
    /** True if the result was capped at `limit` and more rows may exist */
    truncated: boolean;
    /** Applied limit — echoes the query param or its default */
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — ClickHouse analytics tables
// =============================================================================
//
// Same gates as the DO debug surface: platform-admin, audit, rate-limit, kill
// switch. CH tables are queried via parameterized SQL (`{key:Type}` placeholders)
// — never string-interpolated. Allowlist + redaction live in
// workers/backend/src/clickhouse/debug-redaction.ts.

/** Tables an admin can read via `wayai admin analytics`. */
export const dataExplorerDebugAnalyticsTable = z.enum([
  'conversation',
  'message',
  'schedule_event',
]);
export type DataExplorerDebugAnalyticsTable = z.infer<typeof dataExplorerDebugAnalyticsTable>;

/** UUID validator reused for filter values. Any non-UUID input is rejected before reaching CH. */
const debugUuid = z.string().uuid();

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export const dataExplorerDebugAnalyticsRowsParam = z.object({
  table: dataExplorerDebugAnalyticsTable,
});

export const dataExplorerDebugAnalyticsRowsQuery = z.object({
  conversation_id: debugUuid.optional(),
  hub_id: debugUuid.optional(),
  message_id: debugUuid.optional(),
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
});

export type DataExplorerDebugAnalyticsRowsQueryInput = z.infer<typeof dataExplorerDebugAnalyticsRowsQuery>;

/** GET /api/admin/data-explorer/debug/analytics/tables */
export interface DataExplorerDebugAnalyticsTablesResponse {
  data: {
    tables: DataExplorerDebugAnalyticsTable[];
  };
}

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export interface DataExplorerDebugAnalyticsRowsResponse {
  data: {
    table: DataExplorerDebugAnalyticsTable;
    rows: Record<string, unknown>[];
    /** Redacted column names or dotted JSON paths (e.g. "data", "data.meta.last_message_text"). */
    redacted_columns: string[];
    truncated: boolean;
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 archive
// =============================================================================
//
// Admin-gated counterpart to `GET /api/archive/:hubId/conversations/:conversationId`.
// Same archive blob, but reachable only by platform admins and audited as
// `admin_debug_read`. Does not provision access — admins still need the blob to
// exist in R2.

// Hub+conversation pair shared by both debug-archive and debug-observability
// param schemas — keep them as one source of truth so route paths stay aligned.
const debugHubConvParam = z.object({
  hubId: debugUuid,
  conversationId: debugUuid,
});

// Optional message_id filter — same semantics for archive and observability
// (server returns the matching message; otherwise the full set).
const debugMessageIdQuery = z.object({
  message_id: debugUuid.optional(),
});

export const dataExplorerDebugArchiveParam = debugHubConvParam;
export const dataExplorerDebugArchiveQuery = debugMessageIdQuery;

export type DataExplorerDebugArchiveQueryInput = z.infer<typeof dataExplorerDebugArchiveQuery>;

/** GET /api/admin/data-explorer/debug/archive/:hubId/conversations/:conversationId */
export interface DataExplorerDebugArchiveResponse {
  data: {
    hub_id: string;
    conversation_id: string;
    /** The full archive blob written by the archive job, optionally with `messages` filtered server-side. */
    archive: Record<string, unknown>;
    /** True when ?message_id= filtered the messages array. */
    message_filtered: boolean;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 LLM observability
// =============================================================================
//
// Admin-gated read of per-message OpenTelemetry GenAI records (full prompts,
// completions, tool calls, tokens, latency). The hub-scoped equivalent at
// `/api/conversations/:conversationId/...` enforces hub-user / team-member
// access; this debug variant requires `platform:admin` instead so an operator
// can inspect any hub for incident response. Same data shape both ways.

export const dataExplorerDebugObservabilityParam = debugHubConvParam;
export const dataExplorerDebugObservabilityQuery = debugMessageIdQuery;

export type DataExplorerDebugObservabilityQueryInput = z.infer<typeof dataExplorerDebugObservabilityQuery>;

/**
 * GET /api/admin/data-explorer/debug/observability/:hubId/conversations/:conversationId
 *
 * Discriminated by `kind` so TypeScript callers narrow without non-null assertions:
 * - `kind: 'list'` (no ?message_id) — `entries` is populated.
 * - `kind: 'single'` (with ?message_id) — `record` is populated.
 */
export type DataExplorerDebugObservabilityResponse =
  | {
      data: {
        kind: 'list';
        hub_id: string;
        conversation_id: string;
        entries: import('../observability/types').WayAIMetadataRecord[];
        /** True when the list was truncated by the server cap. */
        truncated: boolean;
      };
    }
  | {
      data: {
        kind: 'single';
        hub_id: string;
        conversation_id: string;
        record: import('../observability/types').LLMObservabilityRecord;
      };
    };

// =============================================================================
// Health Sampling — Response Types
// =============================================================================

export interface HealthSamplingInstanceResult {
  namespace: string;
  instance_id: string;
  status: 'pass' | 'fail' | 'error';
  issues: HealthValidationIssue[];
  error?: string;
}

export interface HealthSamplingReport {
  data: {
    status: 'pass' | 'fail';
    triggered_at: string;
    completed_at: string;
    commit?: string;
    sample_size: number;
    results: HealthSamplingInstanceResult[];
    summary: {
      total_instances: number;
      passed: number;
      failed: number;
      errors: number;
    };
  };
}
